import Link from 'next/link';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import {
  useTrackCreditWarningPopoverClicked,
  useTrackCreditWarningPopoverDismissed,
  useTrackCreditWarningPopoverShown,
  useTrackDepletedStateViewed,
  useTrackDepletedUpsellClicked,
} from '@air/analytics';
import { ArrowRightIcon, TriangleAlertIcon } from '@air/design-icons';
import { AlertBanner } from '@air/primitive-alert-banner';
import { useAirModal } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { isCreditUsageDepletedDisplayState } from '~/components/CreditUsageMeter/utils';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { NEW_TAB } from '~/constants/urls';
import { useDismissedCreditUsageAlert } from '~/hooks/creditUsageAlerts/useDismissedCreditUsageAlert';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { getCreditUsageBannerAlertCandidate } from '~/utils/creditUsageAlerts/getCreditUsageBannerAlertCandidate';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export const CreditUsageBannerAlert = memo(() => {
  const { data: subscription } = useSubscriptionContext();
  const { data: creditStats } = useCreditStats();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const [showPlansModal] = useAirModal(PlansModal);
  const { trackDepletedStateViewed } = useTrackDepletedStateViewed();
  const { trackDepletedUpsellClicked } = useTrackDepletedUpsellClicked();
  const { trackCreditWarningPopoverClicked } = useTrackCreditWarningPopoverClicked();
  const { trackCreditWarningPopoverDismissed } = useTrackCreditWarningPopoverDismissed();
  const { trackCreditWarningPopoverShown } = useTrackCreditWarningPopoverShown();
  const creditAnalyticsParams = useCreditAnalyticsParams();
  const lastTrackedImpressionRef = useRef<'depleted' | 'warning' | null>(null);

  const canEditPlan = canManageSubscription(permissions);

  const alertCandidate = useMemo(() => {
    if (!creditStats || !subscription || !permissions) {
      return null;
    }

    if (!creditStats.allocated) {
      return null;
    }

    return getCreditUsageBannerAlertCandidate({
      creditStats,
      subscription,
      canEditPlan,
      showPlansModal: () => showPlansModal({ leadFormTrigger: 'credit_banner' }),
    });
  }, [canEditPlan, creditStats, permissions, showPlansModal, subscription]);

  const { isDismissed, onDismiss } = useDismissedCreditUsageAlert({
    alertType: 'banner',
    creditUsageState: alertCandidate?.creditUsageState,
    nextCycleStartAt: creditStats?.nextCycleStartAt,
    workspaceId: subscription?.workspaceId,
    persistenceType: alertCandidate?.useSessionStorage ? 'sessionStorage' : 'localStorage',
  });

  const isDepleted = isCreditUsageDepletedDisplayState(alertCandidate?.creditUsageState);

  const handleDismiss = useCallback(() => {
    // Only track dismiss for warning states (not depleted) since there's no depleted_dismissed event
    if (!isDepleted && creditAnalyticsParams) {
      trackCreditWarningPopoverDismissed({
        type: 'banner',
        ...creditAnalyticsParams,
      });
    }
    onDismiss();
  }, [creditAnalyticsParams, isDepleted, onDismiss, trackCreditWarningPopoverDismissed]);

  // Single impression effect for both states. Ref tracks the last-fired state so transitions
  // (warning → depleted or vice versa) fire the corresponding new event; resets on dismiss /
  // alert-gone so a fresh cycle re-tracks.
  useEffect(() => {
    if (!alertCandidate || isDismissed || !creditAnalyticsParams) {
      lastTrackedImpressionRef.current = null;
      return;
    }

    const currentState = isDepleted ? 'depleted' : 'warning';

    if (lastTrackedImpressionRef.current === currentState) {
      return;
    }

    lastTrackedImpressionRef.current = currentState;

    if (currentState === 'depleted') {
      trackDepletedStateViewed({
        surface: 'banner',
        ...creditAnalyticsParams,
      });
    } else {
      trackCreditWarningPopoverShown({
        type: 'banner',
        ...creditAnalyticsParams,
      });
    }
  }, [
    alertCandidate,
    creditAnalyticsParams,
    isDepleted,
    isDismissed,
    trackCreditWarningPopoverShown,
    trackDepletedStateViewed,
  ]);

  const handleCtaClick = useCallback(
    (onClick?: () => void) => {
      // Only track upsell/warning clicks when user can actually upgrade (not viewing member list)
      if (canEditPlan && creditAnalyticsParams) {
        if (isDepleted) {
          trackDepletedUpsellClicked({
            surface: 'banner',
            ...creditAnalyticsParams,
          });
        } else {
          trackCreditWarningPopoverClicked({
            type: 'banner',
            ...creditAnalyticsParams,
          });
        }
      }
      onClick?.();
    },
    [canEditPlan, creditAnalyticsParams, isDepleted, trackCreditWarningPopoverClicked, trackDepletedUpsellClicked],
  );

  const ctaButton = useMemo(() => {
    const button = alertCandidate?.ctaButton;

    if (!button) {
      return null;
    }

    if ('href' in button) {
      return (
        <Link
          href={button.href}
          target={button.newTab ? NEW_TAB : undefined}
          rel="noopener noreferrer"
          className="font-semibold text-14 text-inherit"
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
          onClick={() => handleCtaClick()}
        >
          {button.text}
        </Link>
      );
    }

    return (
      <button
        className="font-semibold text-14 text-inherit"
        // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
        onClick={() => handleCtaClick(button.onClick)}
        type="button"
      >
        {button.text}
      </button>
    );
  }, [alertCandidate?.ctaButton, handleCtaClick]);

  if (!alertCandidate || isDismissed) {
    return null;
  }

  const { title, description } = alertCandidate;

  return (
    <AlertBanner
      color={isDepleted ? 'red' : 'yellow'}
      data-testid="CREDITS_DEPLETED_BANNER"
      prefix={
        <TriangleAlertIcon
          className={cn(isDepleted ? 'ads:text-icon-error text-flash-11' : 'ads:text-icon-warning text-amber-11')}
          size="sm"
        />
      }
      onDismiss={handleDismiss}
    >
      <span className="font-semibold">{title}</span> {description}
      {!!ctaButton && (
        <div className="ml-2 inline-flex items-center gap-1">
          {ctaButton}
          <ArrowRightIcon size="sm" />
        </div>
      )}
    </AlertBanner>
  );
});

CreditUsageBannerAlert.displayName = 'CreditUsageBannerAlert';
